import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Switch from '@mui/material/Switch';

import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import ImageIcon from '@mui/icons-material/Image';

import baseStyle from '@ts-core/styles/baseStyles';
import BasePage from '@ts-core/pages/BasePage';
import TSTable from '@ts-core/components/Table/TSTable';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';
import Dialog, { useDialog } from '@ts-core/components/Dialog';

import RoleSelector from 'modules/academies/components/RoleSelector/RoleSelector';
import { useAcademy, useAcademyProfessors } from 'modules/academies/hooks';
import { useProfessorAuth } from 'modules/auth/hooks';

import AcademyForm from '../../components/AcademyForm';
import AddProfessorForm from '../../components/AddAcademyProfessorForm';

const useStyles = makeStyles((theme) => ({
  ...baseStyle(theme),
  nameButton: {
    borderBottom: '1px solid transparent',
    marginBottom: 8,
    '&:hover': {
      borderBottom: '1px solid black',
    },
  },
  logo: {
    height: '100%',
    width: 'auto',
  },
  logoPlaceholderIcon: {
    fontSize: 64,
    color: '#a0a0a0',
  },
  logoContainer: {
    '&:hover': {
      backgroundColor: '#dddddd',
      cursor: 'pointer',
    },
  },
}));

const AcademyPage = () => {
  const intl = useIntl();

  const { user } = useProfessorAuth();

  const classes = useStyles();
  const { academy } = useAcademy();
  const {
    professors,
    loadingProfessors,
    roles = [],
    onToggleProfessor,
    onSetProfessorRole,
    onAddAcademyProfessor,
    addAcademyProfessorLoading,
    addAcademyProfessorError,
  } = useAcademyProfessors();

  /**
   * Edit Academy
   */
  const { onOpenDialog: onOpenEditAcademyDialog, onCloseDialog: onCloseEditAcademyDialog, dialogProps: editAcademyDialogProps } = useDialog();
  const handleEditAcademyName = useCallback(() => {
    onOpenEditAcademyDialog();
  }, [onOpenEditAcademyDialog]);

  /**
   * Add professor
   */
  const { onOpenDialog: onOpenAddProfessorDialog, onCloseDialog: onCloseAddProfessorDialog, dialogProps: addProfessorDialogProps } = useDialog();
  const handleOpenAddProfessorDialog = useCallback(() => {
    onOpenAddProfessorDialog({
      title: intl.formatMessage({ id: 'academy.addProfessor' }),
    });
  }, [onOpenAddProfessorDialog, intl]);
  const handleAddAcademyProfessor = useCallback(
    (input) => {
      onAddAcademyProfessor(input, () => onCloseAddProfessorDialog());
    },
    [onAddAcademyProfessor, onCloseAddProfessorDialog]
  );

  /**
   * Set Role
   */
  const handleSetRole = useCallback(
    (professor) => (newRole) => {
      onSetProfessorRole(professor, newRole);
    },
    [onSetProfessorRole]
  );

  /**
   * Toggle professor
   */
  const handleToggleProfessor = useCallback(
    (professor) => () => {
      onToggleProfessor(professor);
    },
    [onToggleProfessor]
  );

  const isCurrentUser = useCallback(
    (professor) => {
      return professor.id === user._id;
    },
    [user]
  );

  const isActiveCell = useCallback(
    (value, column, item) => {
      if (isCurrentUser(item)) return null;
      return <Switch checked={item.active} onClick={(event) => event.stopPropagation()} onChange={handleToggleProfessor(item)} color="primary" />;
    },
    [isCurrentUser, handleToggleProfessor]
  );

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'studentsTable.column.name' }),
        value: intl.formatMessage({ id: 'studentsTable.column.name' }),
        accessor: 'name',
        sortable: true,
      },
      {
        Header: intl.formatMessage({ id: 'studentsTable.column.email' }),
        value: intl.formatMessage({ id: 'studentsTable.column.email' }),
        accessor: 'email',
        sortable: true,
      },
      {
        Header: intl.formatMessage({ id: 'table.role' }),
        value: intl.formatMessage({ id: 'table.role' }),
        accessor: 'role',
        valueTransformer: (role, column, professor) => {
          return isCurrentUser(professor) ? (
            intl.formatMessage({ id: `role.${role.name.toLowerCase()}` })
          ) : (
            <RoleSelector professor={professor} roles={roles} selectedRole={role} onRoleSelected={handleSetRole(professor)} />
          );
        },

        exportable: false,
      },
      {
        Header: 'Activo',
        accessor: 'active',
        valueTransformer: isActiveCell,
      },
    ],
    [intl, handleSetRole, isCurrentUser, isActiveCell, roles]
  );

  const speedDialOptions = useMemo(
    () => [
      {
        icon: <PersonAddIcon />,
        title: <FormattedMessage id="academy.addProfessor" />,
        onClick: handleOpenAddProfessorDialog,
      },
    ],
    [handleOpenAddProfessorDialog]
  );

  return (
    <BasePage>
      <Box display="flex" flexDirection="row" justifyContent="center " mb={2}>
        <Box width={200} height={200} display="flex" flexDirection="row" justifyContent="center ">
          {academy?.logoImageUrl ? (
            <img src={academy.logoImageUrl} alt="logo" className={classes.logo} />
          ) : (
            <Box
              width={200}
              height={200}
              borderRadius="100px"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              bgcolor="#f5f5f5"
              className={classes.logoContainer}
              onClick={handleEditAcademyName}
            >
              <ImageIcon className={classes.logoPlaceholderIcon} />
            </Box>
          )}
        </Box>
      </Box>
      <Box textAlign="center" width="100%" mb={2}>
        <ButtonBase className={classes.nameButton} onClick={handleEditAcademyName}>
          <Typography variant="h4" display="inline">
            {academy?.name}
          </Typography>
          <EditIcon style={{ marginLeft: 8, marginBottom: 4 }} />
        </ButtonBase>
      </Box>
      <TSTable data={professors} columns={columns} loading={loadingProfessors} />
      <Dialog {...editAcademyDialogProps}>
        <AcademyForm academy={academy} onAcademySaved={onCloseEditAcademyDialog} />
      </Dialog>
      <Dialog {...addProfessorDialogProps}>
        {(data) => (
          <AddProfessorForm
            submitError={addAcademyProfessorError}
            submitting={addAcademyProfessorLoading}
            roles={roles}
            onSubmit={handleAddAcademyProfessor}
          />
        )}
      </Dialog>
      <SpeedDialMenu options={speedDialOptions} />
    </BasePage>
  );
};

export default AcademyPage;
